import type * as React from 'react'
import { registerBinaryExtensions, areExtensionsCompatible } from '@papan/types'

// Re-export so consumers can import from either @papan/collab/client or @papan/types
export { areExtensionsCompatible }

// ─── Client Editor Registry ─────────────────────────────────────────
//
// Each editor registers its UI component and capabilities.
// TabContent.tsx uses this registry to route file types to editors.

/** Undo/redo state reported by active editors.
 *  Structurally mirrors core-ui's UndoProvider — keep the two in step. */
export interface EditorUndoRedoState {
  canUndo: boolean
  canRedo: boolean
}

/** Undo/redo interface that editors can register with the app's Edit menu */
export interface EditorUndoRedo {
  undo: () => Promise<boolean>
  redo: () => Promise<boolean>
  label?: string
  /** This surface can be CHANGED by the user, so Ctrl+Z belongs to it while
   *  they are in it — even when its own history is momentarily empty. Unset
   *  only on a viewer that cannot be edited. */
  editable?: boolean
  /** Subscribe to undo/redo availability changes. Returns unsubscribe fn. */
  onStateChange?: (cb: (state: EditorUndoRedoState) => void) => () => void
}

/**
 * #249: show line `line` (1-based) — a chat link `app.py:42`. `seq` makes each
 * request new, so clicking the same link twice reveals it twice. Text editors
 * switch to their source view, scroll the line into view and flash it; others
 * ignore it.
 */
export interface RevealLineRequest {
  line: number
  seq: number
}

let revealSeq = 0
/** A fresh request for `line`. */
export function revealLineRequest(line: number): RevealLineRequest {
  return { line, seq: ++revealSeq }
}

/** Standard props passed to collaborative editors */
export interface EditorComponentProps {
  room: string
  fileId: string
  fileName?: string
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Register undo/redo with the app's Edit menu. Returns unregister fn. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** Export a generated blob (e.g. PDF) to the drive via the Save As dialog */
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null) => void
  /** #249: the line to show (see RevealLineRequest). */
  revealLine?: RevealLineRequest
}

/** Text selection captured from an editor */
export interface EditorSelection {
  text: string
  fileName: string
  filePath: string      // filled by caller (WorkspaceView) from tab.path when the editor left it empty
  fileId: string
  startLine: number     // 1-based; 0 if unknown (e.g. Collabora)
  endLine: number       // 1-based; 0 if unknown
  /** Selection context type — determines chip label + tooltip format */
  kind: 'lines' | 'cells' | 'text' | 'slide' | 'element'
  /** Cell range for spreadsheets, e.g. "A1:C5", "1:5", "A:C" */
  cellRange?: string
  /** Sheet name for spreadsheets */
  sheetName?: string
  /** 1-based slide number for presentations */
  slideNumber?: number
  /** 1-based page number for text documents (Writer) */
  pageNumber?: number
  /** (kind 'element') Tag name of the selected DOM element, e.g. "div" */
  elementTag?: string
  /** (kind 'element') Short selector label, e.g. "div#hero.card" */
  elementSelector?: string
  /** (kind 'element') Ancestor breadcrumb, e.g. "body › section.hero › div.card" */
  elementPath?: string
}

/**
 * Stable dedup fingerprint for a selection. Consumers compare it to decide whether the
 * selection actually CHANGED before re-rendering the chip / re-sending AI context. It
 * MUST include EVERY semantic field AND the text CONTENT (not just length) — any can be the
 * sole difference: text (A1->B2 between equal-length values, or an in-place "10"->"20" edit);
 * sheetName/cellRange (Sheet1!A1->Sheet2!A1); filePath/fileName/elementPath (a design pick on
 * a multi-page app's sub-page keeps the ROOT fileId + identical selector/lines but a different
 * served filePath, so dropping it points the AI at the wrong file). Serialized as a JSON ARRAY
 * so no field value can collide across a boundary. Two consumers (WorkspaceView, ChatHomeView)
 * had drifting hand-rolled keys that each dropped different fields — this is the ONE
 * spelling; use it in both. `` separates fields so `:` in content can't collide.
 */
export function selectionFingerprint(sel: EditorSelection | null): string {
  if (!sel) return ''
  return JSON.stringify([
    sel.fileId,
    sel.filePath || '',
    sel.fileName || '',
    sel.kind,
    sel.startLine,
    sel.endLine,
    sel.cellRange || '',
    sel.sheetName || '',
    sel.pageNumber ?? '',
    sel.slideNumber ?? '',
    sel.elementTag || '',
    sel.elementSelector || '',
    sel.elementPath || '',
    sel.text,
  ])
}

/** Edit actions exposed by collaborative editors via ref */
export interface EditorEditHandle {
  copy: () => void
  cut: () => void
  paste: () => void
  delete: () => void
  /** Return the current text selection, or null if none / unsupported */
  getSelection?: () => EditorSelection | null
  /** Focus the editor (e.g. re-click active tab to restore blurred selection) */
  focusEditor?: () => void
  /** Format Document — text editors only; absent where there is nothing to format. */
  format?: () => void
}

/** Read-only viewer props */
export interface ViewerComponentProps {
  fileId: string
  fileName?: string
}

/**
 * WHAT KIND of editor a registration is — the one fact a surface other than the
 * drive needs to decide how (and whether) it can mount the registry's choice.
 *
 * The share view (apps/web ShareViewerChrome, via `chooseShareEditor`) asks the
 * registry which editor a file gets and then applies its own CAPS per family
 * (read-only, no Run, …). Keying those caps on the family rather than on file
 * extensions is what keeps the two surfaces from drifting: a new extension added
 * to a registration reaches the share view with no second list to update, and a
 * new FAMILY fails to compile until `chooseShareEditor` decides what it does.
 */
export type EditorFamily =
  /** Y.Doc text editor (MarkdownEditor) — md/txt/json/pmeta + the txt fallback. */
  | 'text'
  /** HtmlEditor (source + the running page). */
  | 'html-page'
  /** ScriptEditor (Src ↔ Run). */
  | 'script'
  /** CSV/TSV SpreadsheetEditor (Univer edit / grid view). */
  | 'spreadsheet'
  /** SQLite DatabaseEditor over /api/sqlite. */
  | 'sqlite'
  /** Firebird DatabaseEditor over /api/fdb (credential-authenticated). */
  | 'firebird'
  /** Read-only data grid (parquet). */
  | 'datagrid'
  /** Image / video / audio / pdf MediaEditor wrapper. */
  | 'media'
  /** Office under a DocsAPI engine (OnlyOffice / EuroOffice). */
  | 'office-docsapi'
  /** Office under Collabora (WOPI). */
  | 'office-collabora'
  /** Plain read-only text viewer (ReadOnlyText). */
  | 'text-viewer'
  /** The PDApp folder editor (never in the extension map). */
  | 'app'

export interface EditorRegistration {
  /** What kind of editor this is — see EditorFamily. */
  family: EditorFamily
  /** Unique editor ID (e.g., 'univer-sheet', 'onlyoffice-sheet'). Optional for single-editor extensions. */
  id?: string
  /** File extensions this editor handles (lowercase, no dot) */
  extensions: string[]
  /** Display name */
  name: string
  /** React component — receives EditorComponentProps (collab) or ViewerComponentProps (read-only) */
  component: React.ComponentType<any>
  /** Max file size in bytes before showing "too large" guard (collab editors only) */
  maxEditableSize?: number
  /** If true, component is a read-only viewer (no Yjs, no dirty tracking) */
  readOnly?: boolean
  /** Subset of extensions that are binary formats (incompatible with text-based renames) */
  binaryExtensions?: string[]
  /** Default props to spread onto the editor component (e.g., { adapter: firebirdAdapter }) */
  defaultProps?: Record<string, unknown>
  /** #823: the editor shows its own "moved to the bin" / "being deleted" notice (its live session tells it — a Yjs
   *  room's close, an office document's channel). Unset: the surface holding it shows the notice instead. */
  notifiesFileGone?: boolean
}

// ─── Registry ───────────────────────────────────────────────────────

const registrations: EditorRegistration[] = []
// Multi-editor support: each extension maps to an ordered array of registrations.
// The first entry is the default; user preferences can select an alternative.
const extensionMap = new Map<string, EditorRegistration[]>()
const idMap = new Map<string, EditorRegistration>()

export function registerEditor(registration: EditorRegistration): void {
  registrations.push(registration)
  for (const ext of registration.extensions) {
    const key = ext.toLowerCase()
    const list = extensionMap.get(key) ?? []
    list.push(registration)
    extensionMap.set(key, list)
  }
  if (registration.id) {
    idMap.set(registration.id, registration)
  }
  if (registration.binaryExtensions) {
    registerBinaryExtensions(registration.binaryExtensions)
  }
}

/** Look up the default editor for a file extension */
export function getEditorForExtension(ext: string): EditorRegistration | null {
  const list = extensionMap.get(ext.toLowerCase())
  return list?.[0] ?? null
}

/** Get all registered editors for an extension (for editor picker UI) */
export function getAllEditorsForExtension(ext: string): EditorRegistration[] {
  return extensionMap.get(ext.toLowerCase()) ?? []
}

/** Look up an editor by its unique ID */
export function getEditorById(id: string): EditorRegistration | null {
  return idMap.get(id) ?? null
}

/** Get the preferred editor for an extension, considering user preferences */
export function getPreferredEditor(ext: string, preferredId?: string): EditorRegistration | null {
  if (preferredId) {
    const preferred = idMap.get(preferredId)
    if (preferred) return preferred
  }
  return getEditorForExtension(ext)
}

/** Check if an extension has a registered editor */
export function isRegisteredExtension(ext: string): boolean {
  return extensionMap.has(ext.toLowerCase())
}

/** Get all registered extensions */
export function getRegisteredExtensions(): string[] {
  return Array.from(extensionMap.keys())
}

// ─── Preview Registry ─────────────────────────────────────────────
//
// Previews are read-only viewers for version history / standalone view.
// They receive a fileId + versionId and render content without Yjs.

/** Props passed to preview viewer components */
export interface PreviewComponentProps {
  fileId: string
  fileName?: string
  versionId: string
  viewSync?: Record<string, Record<string, unknown>>
}

export interface PreviewRegistration {
  /** File extensions this preview handles (lowercase, no dot) */
  extensions: string[]
  /** Display name */
  name: string
  /** React component — receives PreviewComponentProps */
  component: React.ComponentType<PreviewComponentProps>
  /** If true, component handles version changes internally via refreshFile (skip React remount) */
  supportsVersionRefresh?: boolean
}

const previewMap = new Map<string, PreviewRegistration>()

export function registerPreview(registration: PreviewRegistration): void {
  for (const ext of registration.extensions) {
    previewMap.set(ext.toLowerCase(), registration)
  }
}

export function getPreviewForExtension(ext: string): PreviewRegistration | null {
  return previewMap.get(ext.toLowerCase()) ?? null
}

/** Get all registered preview extensions */
export function getRegisteredPreviewExtensions(): string[] {
  return Array.from(previewMap.keys())
}

